import Link from "next/link";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { useAuthenticatedUserClassrooms } from "@sol/classrooms";
import { interpolate, Route } from "@sol/routing";
import { getResourceUuid } from "@sol/sdk";
import { ArrowRight } from "@sol/uikit/core/icons/Arrows/ArrowRight";
import { Result } from "@sol/uikit/feedback/Result/Result";
import { Button } from "@sol/uikit/general/Button/Button";

import { lastStartedClassroom } from "@/entities/classroom";

import { HomeCard, HomeCardDescription } from "./components/HomeCard";

export function LearnerHome() {
    const { classrooms } = useAuthenticatedUserClassrooms();
    const [t] = useTranslation();

    const today = useMemo(() => new Date(), []);

    const classroom = lastStartedClassroom(classrooms, today);

    if (!classroom) {
        return (
            <Result
                status="info"
                title={t("page.home.learner.noActiveClassroom.title")}
                subTitle={t("page.home.learner.noActiveClassroom.description")}
            />
        );
    }

    const classroomUUID = getResourceUuid(classroom);

    return (
        <div className="flex w-full gap-4 max-lg:flex-col">
            <div className="flex flex-col gap-4 lg:w-1/2">
                <HomeCard
                    title={t("component.home.learner.work.title")}
                    className="min-h-0 flex-1"
                    image={<img src="/static/home/planning.svg" alt="" />}
                    action={
                        <Link href={Route.MY_WORKSPACE} passHref>
                            <Button
                                size="small"
                                iconPosition="end"
                                icon={<ArrowRight variant="antd" />}
                                title={t("component.home.learner.work.cta.title")}
                                aria-label={t("component.home.learner.work.cta.title")}
                            >
                                {t("component.home.learner.work.cta.label")}
                            </Button>
                        </Link>
                    }
                >
                    <HomeCardDescription label={t("component.home.learner.work.label")}>
                        {t("component.home.learner.work.description")}
                    </HomeCardDescription>
                </HomeCard>
                <HomeCard
                    title={t("component.home.learner.agenda.title")}
                    image={<img src="/static/home/schedule.svg" alt="" />}
                    action={
                        <Link href={interpolate(Route.CLASSROOM_AGENDA, { classroomId: classroomUUID })} passHref>
                            <Button
                                size="small"
                                iconPosition="end"
                                icon={<ArrowRight variant="antd" />}
                                title={t("component.home.learner.agenda.cta.title")}
                                aria-label={t("component.home.learner.agenda.cta.title")}
                            >
                                {t("component.home.learner.agenda.cta.label")}
                            </Button>
                        </Link>
                    }
                >
                    <HomeCardDescription label={t("component.home.learner.agenda.label")}>
                        {t("component.home.learner.agenda.description")}
                    </HomeCardDescription>
                </HomeCard>
            </div>
            <div className="flex flex-col gap-4 lg:w-1/2">
                <HomeCard
                    title={t("component.home.learner.self-evaluation.title")}
                    className="min-h-0 flex-1"
                    image={<img src="/static/home/analytics-2.svg" alt="" />}
                    action={
                        <Link href={interpolate(Route.FOLLOW_UPS_LIST, {})} passHref>
                            <Button
                                size="small"
                                iconPosition="end"
                                icon={<ArrowRight variant="antd" />}
                                title={t("component.home.learner.self-evaluation.cta.title")}
                                aria-label={t("component.home.learner.self-evaluation.cta.title")}
                            >
                                {t("component.home.learner.self-evaluation.cta.label")}
                            </Button>
                        </Link>
                    }
                >
                    <HomeCardDescription label={t("component.home.learner.self-evaluation.label")}>
                        {t("component.home.learner.self-evaluation.description")}
                    </HomeCardDescription>
                </HomeCard>
                <HomeCard
                    title={t("component.home.learner.resources.title")}
                    action={
                        <Link
                            href={interpolate(Route.CLASSROOM_RESOURCES, {
                                classroomId: classroomUUID,
                            })}
                            passHref
                        >
                            <Button
                                size="small"
                                iconPosition="end"
                                icon={<ArrowRight variant="antd" />}
                                title={t("component.home.learner.resources.cta.title")}
                                aria-label={t("component.home.learner.resources.cta.title")}
                            >
                                {t("component.home.learner.resources.cta.label")}
                            </Button>
                        </Link>
                    }
                >
                    <HomeCardDescription label={t("component.home.learner.resources.label")}>
                        {t("component.home.learner.resources.description")}
                    </HomeCardDescription>
                </HomeCard>
            </div>
        </div>
    );
}
